import { RouterReducerState } from '@ngrx/router-store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RouterStateUrl } from '@sd-portal/shared/util';
import { State } from '..';
import * as fromTrip from './trip.reducer';

// feature selectors
const getSharedAuditState = createFeatureSelector<State>('sharedAudit');
const getRouterState = createFeatureSelector<RouterReducerState<RouterStateUrl>>('router');

// feature slice - scheduled trip
const getTripSubState = createSelector(getSharedAuditState, state => state.trip);

// scheduled trip slice selectors
const getDeletedScheduledAircraftTripSkeletonsState = createSelector(
  getTripSubState,
  state => state.deletedScheduledAircraftTripSkeletons
);
const getScheduledAircraftTripSkeletonState = createSelector(
  getTripSubState,
  state => state.scheduledAircraftTripSkeleton
);
const getScheduledAircraftTripState = createSelector(
  getTripSubState,
  state => state.scheduledAircraftTripAudit
);
const getScheduledFlightAuditState = createSelector(
  getTripSubState,
  state => state.scheduledFlightAudit
);
const getFlightAuditState = createSelector(getTripSubState, state => state.flightAudit);
const getFlightPassengerAuditState = createSelector(
  getTripSubState,
  state => state.flightPassengerAudit
);
const getFlightPassengerCateringAuditState = createSelector(
  getTripSubState,
  state => state.flightPassengerCateringAudit
);
const getFlightPassengerHotelAuditState = createSelector(
  getTripSubState,
  state => state.flightPassengerHotelAudit
);
const getFlightPassengerTransportationAuditState = createSelector(
  getTripSubState,
  state => state.flightPassengerTransportationAudit
);
const getFlightCrewMemberAuditState = createSelector(
  getTripSubState,
  state => state.flightCrewMemberAudit
);
const getFlightCrewMemberCateringAuditState = createSelector(
  getTripSubState,
  state => state.flightCrewMemberCateringAudit
);
const getFlightCrewMemberHotelAuditState = createSelector(
  getTripSubState,
  state => state.flightCrewMemberHotelAudit
);
const getFlightCrewMemberTransportationAuditState = createSelector(
  getTripSubState,
  state => state.flightCrewMemberTransportationAudit
);
const getFlightBusinessPersonAuditState = createSelector(
  getTripSubState,
  state => state.flightBusinessPersonAudit
);
const getScheduledEventAuditState = createSelector(
  getTripSubState,
  state => state.scheduledEventAudit
);
const getLegNoteAuditState = createSelector(getTripSubState, state => state.legNoteAudit);
const getFlightFuelAuditState = createSelector(getTripSubState, state => state.flightFuelAudit);
const getDeletedTripsState = createSelector(getTripSubState, state => state.deletedTrips);
const getTripLogsState = createSelector(getTripSubState, state => state.tripLogs);
const getDeletedTripLegsState = createSelector(getTripSubState, state => state.deletedTripLegs);
const getTripLegsState = createSelector(getTripSubState, state => state.tripLegs);
const getLegEventLogsState = createSelector(getTripSubState, state => state.legEventLogs);
const getLegFlightLogsState = createSelector(getTripSubState, state => state.legFlightLogs);
const getLegFuelLogsState = createSelector(getTripSubState, state => state.legFuelLogs);

// deleted scheduled aircraft trip skeletons
export const getDeletedScheduledTripSkeletonsLoaded = createSelector(
  getDeletedScheduledAircraftTripSkeletonsState,
  state => state.loaded
);
export const getDeletedScheduledTripSkeletonsLoading = createSelector(
  getDeletedScheduledAircraftTripSkeletonsState,
  state => state.loading
);
export const getDeletedScheduledTripSkeletons = createSelector(
  getDeletedScheduledAircraftTripSkeletonsState,
  fromTrip.deletedScheduledAircraftTripSkeletonSelectors.selectAll
);

// router param selector
export const getScheduledAircraftTripId = createSelector(
  getRouterState,
  state => +state.state.params['id']
);

// scheduled aircraft trip skeleton
export const getScheduledAircraftTripSkeletonLoaded = createSelector(
  getScheduledAircraftTripSkeletonState,
  state => state.loaded
);
export const getScheduledAircraftTripSkeletonLoading = createSelector(
  getScheduledAircraftTripSkeletonState,
  state => state.loading
);
export const getScheduledAircraftTripSkeleton = createSelector(
  getScheduledAircraftTripSkeletonState,
  getScheduledAircraftTripSkeletonLoaded,
  getScheduledAircraftTripId,
  (state, isLoaded, scheduledAircraftTripId) => {
    return isLoaded ? state.entities[scheduledAircraftTripId] : null;
  }
);

// scheduled aircraft trip audit
export const getScheduledAircraftTripAuditLoaded = createSelector(
  getScheduledAircraftTripState,
  state => state.loaded
);
export const getScheduledAircraftTripAuditLoading = createSelector(
  getScheduledAircraftTripState,
  state => state.loading
);
export const getScheduledAircraftTripAudits = createSelector(
  getScheduledAircraftTripState,
  fromTrip.scheduledAircraftTripAuditSelectors.selectAll
);

// scheduled flight audit
export const getScheduledFlightAuditLoaded = createSelector(
  getScheduledFlightAuditState,
  state => state.loaded
);
export const getScheduledFlightAuditLoading = createSelector(
  getScheduledFlightAuditState,
  state => state.loading
);
export const getScheduledFlightAudits = createSelector(
  getScheduledFlightAuditState,
  fromTrip.scheduledFlightAuditSelectors.selectAll
);

// flight audit
export const getFlightAuditLoaded = createSelector(getFlightAuditState, state => state.loaded);
export const getFlightAuditLoading = createSelector(getFlightAuditState, state => state.loading);
export const getFlightAudits = createSelector(
  getFlightAuditState,
  fromTrip.flightAuditSelectors.selectAll
);

// flight passenger audit
export const getFlightPassengerAuditLoaded = createSelector(
  getFlightPassengerAuditState,
  state => state.loaded
);
export const getFlightPassengerAuditLoading = createSelector(
  getFlightPassengerAuditState,
  state => state.loading
);
export const getFlightPassengerAudits = createSelector(
  getFlightPassengerAuditState,
  fromTrip.flightPassengerAuditSelectors.selectAll
);

// flight passenger catering audit
export const getFlightPassengerCateringAuditLoaded = createSelector(
  getFlightPassengerCateringAuditState,
  state => state.loaded
);
export const getFlightPassengerCateringAuditLoading = createSelector(
  getFlightPassengerCateringAuditState,
  state => state.loading
);
export const getFlightPassengerCateringAudits = createSelector(
  getFlightPassengerCateringAuditState,
  fromTrip.flightPassengerCateringAuditSelectors.selectAll
);

// flight passenger hotel audit
export const getFlightPassengerHotelAuditLoaded = createSelector(
  getFlightPassengerHotelAuditState,
  state => state.loaded
);
export const getFlightPassengerHotelAuditLoading = createSelector(
  getFlightPassengerHotelAuditState,
  state => state.loading
);
export const getFlightPassengerHotelAudits = createSelector(
  getFlightPassengerHotelAuditState,
  fromTrip.flightPassengerHotelAuditSelectors.selectAll
);

// flight passenger transportation audit
export const getFlightPassengerTransportationAuditLoaded = createSelector(
  getFlightPassengerTransportationAuditState,
  state => state.loaded
);
export const getFlightPassengerTransportationAuditLoading = createSelector(
  getFlightPassengerTransportationAuditState,
  state => state.loading
);
export const getFlightPassengerTransportationAudits = createSelector(
  getFlightPassengerTransportationAuditState,
  fromTrip.flightPassengerTransportationAuditSelectors.selectAll
);

// flight crew member audit
export const getFlightCrewMemberAuditLoaded = createSelector(
  getFlightCrewMemberAuditState,
  state => state.loaded
);
export const getFlightCrewMemberAuditLoading = createSelector(
  getFlightCrewMemberAuditState,
  state => state.loading
);
export const getFlightCrewMemberAudits = createSelector(
  getFlightCrewMemberAuditState,
  fromTrip.flightCrewMemberAuditSelectors.selectAll
);

// flight crew member catering audit
export const getFlightCrewMemberCateringAuditLoaded = createSelector(
  getFlightCrewMemberCateringAuditState,
  state => state.loaded
);
export const getFlightCrewMemberCateringAuditLoading = createSelector(
  getFlightCrewMemberCateringAuditState,
  state => state.loading
);
export const getFlightCrewMemberCateringAudits = createSelector(
  getFlightCrewMemberCateringAuditState,
  fromTrip.flightCrewMemberCateringAuditSelectors.selectAll
);

// flight crew member hotel audit
export const getFlightCrewMemberHotelAuditLoaded = createSelector(
  getFlightCrewMemberHotelAuditState,
  state => state.loaded
);
export const getFlightCrewMemberHotelAuditLoading = createSelector(
  getFlightCrewMemberHotelAuditState,
  state => state.loading
);
export const getFlightCrewMemberHotelAudits = createSelector(
  getFlightCrewMemberHotelAuditState,
  fromTrip.flightCrewMemberHotelAuditSelectors.selectAll
);

// flight crew member transportation audit
export const getFlightCrewMemberTransportationAuditLoaded = createSelector(
  getFlightCrewMemberTransportationAuditState,
  state => state.loaded
);
export const getFlightCrewMemberTransportationAuditLoading = createSelector(
  getFlightCrewMemberTransportationAuditState,
  state => state.loading
);
export const getFlightCrewMemberTransportationAudits = createSelector(
  getFlightCrewMemberTransportationAuditState,
  fromTrip.flightCrewMemberTransportationAuditSelectors.selectAll
);

// flight business person audit
export const getFlightBusinessPersonAuditLoaded = createSelector(
  getFlightBusinessPersonAuditState,
  state => state.loaded
);
export const getFlightBusinessPersonAuditLoading = createSelector(
  getFlightBusinessPersonAuditState,
  state => state.loading
);
export const getFlightBusinessPersonAudits = createSelector(
  getFlightBusinessPersonAuditState,
  fromTrip.flightBusinessPersonAuditSelectors.selectAll
);

// scheduled event audit
export const getScheduledEventAuditLoaded = createSelector(
  getScheduledEventAuditState,
  state => state.loaded
);
export const getScheduledEventAuditLoading = createSelector(
  getScheduledEventAuditState,
  state => state.loading
);
export const getScheduledEventAudits = createSelector(
  getScheduledEventAuditState,
  fromTrip.scheduledEventAuditSelectors.selectAll
);

// leg note audit
export const getLegNoteAuditLoaded = createSelector(getLegNoteAuditState, state => state.loaded);
export const getLegNoteAuditLoading = createSelector(getLegNoteAuditState, state => state.loading);
export const getLegNoteAudits = createSelector(
  getLegNoteAuditState,
  fromTrip.scheduledEventAuditSelectors.selectAll
);

// flight fuel audit
export const getFlightFuelAuditLoaded = createSelector(
  getFlightFuelAuditState,
  state => state.loaded
);
export const getFlightFuelAuditLoading = createSelector(
  getFlightFuelAuditState,
  state => state.loading
);
export const getFlightFuelAudits = createSelector(
  getFlightFuelAuditState,
  fromTrip.flightFuelAuditSelectors.selectAll
);

// V2 Selectors for lazy loading

// deleted trips
export const getDeletedTripsLoaded = createSelector(getDeletedTripsState, state => state.loaded);
export const getDeletedTripsLoading = createSelector(getDeletedTripsState, state => state.loading);
export const getDeletedTrips = createSelector(
  getDeletedTripsState,
  fromTrip.deletedTripsSelectors.selectAll
);

// trip logs
export const getTripLogsLoaded = createSelector(getTripLogsState, state => state.loaded);
export const getTripLogsLoading = createSelector(getTripLogsState, state => state.loading);
export const getTripLogs = createSelector(getTripLogsState, fromTrip.tripLogsSelectors.selectAll);
export const getTripLogsHasMore = createSelector(getTripLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.tripLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// deleted trip legs
export const getDeletedTripLegsLoaded = createSelector(
  getDeletedTripLegsState,
  state => state.loaded
);
export const getDeletedTripLegsLoading = createSelector(
  getDeletedTripLegsState,
  state => state.loading
);
export const getDeletedTripLegs = createSelector(
  getDeletedTripLegsState,
  fromTrip.deletedTripLegsSelectors.selectAll
);
export const getDeletedTripLegsHasMore = createSelector(getDeletedTripLegsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.deletedTripLegsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// trip legs
export const getTripLegsLoaded = createSelector(getTripLegsState, state => state.loaded);
export const getTripLegsLoading = createSelector(getTripLegsState, state => state.loading);
export const getTripLegs = createSelector(getTripLegsState, fromTrip.tripLegsSelectors.selectAll);
export const getTripLegsHasMore = createSelector(getTripLegsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.tripLegsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// leg event logs
export const getLegEventLogsLoaded = createSelector(getLegEventLogsState, state => state.loaded);
export const getLegEventLogsLoading = createSelector(getLegEventLogsState, state => state.loading);
export const getLegEventLogs = createSelector(
  getLegEventLogsState,
  fromTrip.legEventLogsSelectors.selectAll
);
export const getLegEventLogsHasMore = createSelector(getLegEventLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.legEventLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// leg flight logs
export const getLegFlightLogsLoaded = createSelector(getLegFlightLogsState, state => state.loaded);
export const getLegFlightLogsLoading = createSelector(getLegFlightLogsState, state => state.loading);
export const getLegFlightLogs = createSelector(
  getLegFlightLogsState,
  fromTrip.legFlightLogsSelectors.selectAll
);
export const getLegFlightLogsHasMore = createSelector(getLegFlightLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.legFlightLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// leg fuel logs
export const getLegFuelLogsLoaded = createSelector(getLegFuelLogsState, state => state.loaded);
export const getLegFuelLogsLoading = createSelector(getLegFuelLogsState, state => state.loading);
export const getLegFuelLogs = createSelector(
  getLegFuelLogsState,
  fromTrip.legFuelLogsSelectors.selectAll
);
export const getLegFuelLogsHasMore = createSelector(getLegFuelLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.legFuelLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// flight crew members
const getFlightCrewMembersState = createSelector(getTripSubState, state => state.flightCrewMembers);
export const getFlightCrewMembersLoaded = createSelector(getFlightCrewMembersState, state => state.loaded);
export const getFlightCrewMembersLoading = createSelector(getFlightCrewMembersState, state => state.loading);
export const getFlightCrewMembers = createSelector(getFlightCrewMembersState, state =>
  fromTrip.flightCrewMembersSelectors.selectAll(state)
);
export const getFlightCrewMembersHasMore = createSelector(getFlightCrewMembersState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.flightCrewMembersSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// crew logs
const getCrewLogsState = createSelector(getTripSubState, state => state.crewLogs);
export const getCrewLogsLoaded = createSelector(getCrewLogsState, state => state.loaded);
export const getCrewLogsLoading = createSelector(getCrewLogsState, state => state.loading);
export const getCrewLogs = createSelector(getCrewLogsState, fromTrip.crewLogsSelectors.selectAll);
export const getCrewLogsHasMore = createSelector(getCrewLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.crewLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// crew catering logs
const getCrewCateringLogsState = createSelector(getTripSubState, state => state.crewCateringLogs);
export const getCrewCateringLogsLoaded = createSelector(getCrewCateringLogsState, state => state.loaded);
export const getCrewCateringLogsLoading = createSelector(getCrewCateringLogsState, state => state.loading);
export const getCrewCateringLogs = createSelector(getCrewCateringLogsState, fromTrip.crewCateringLogsSelectors.selectAll);
export const getCrewCateringLogsHasMore = createSelector(getCrewCateringLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.crewCateringLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// crew hotel logs
const getCrewHotelLogsState = createSelector(getTripSubState, state => state.crewHotelLogs);
export const getCrewHotelLogsLoaded = createSelector(getCrewHotelLogsState, state => state.loaded);
export const getCrewHotelLogsLoading = createSelector(getCrewHotelLogsState, state => state.loading);
export const getCrewHotelLogs = createSelector(getCrewHotelLogsState, fromTrip.crewHotelLogsSelectors.selectAll);
export const getCrewHotelLogsHasMore = createSelector(getCrewHotelLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.crewHotelLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// crew transportation logs
const getCrewTransportationLogsState = createSelector(getTripSubState, state => state.crewTransportationLogs);
export const getCrewTransportationLogsLoaded = createSelector(getCrewTransportationLogsState, state => state.loaded);
export const getCrewTransportationLogsLoading = createSelector(getCrewTransportationLogsState, state => state.loading);
export const getCrewTransportationLogs = createSelector(getCrewTransportationLogsState, fromTrip.crewTransportationLogsSelectors.selectAll);
export const getCrewTransportationLogsHasMore = createSelector(getCrewTransportationLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.crewTransportationLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// flight passengers
const getFlightPassengersState = createSelector(getTripSubState, state => state.flightPassengers);
export const getFlightPassengersLoaded = createSelector(getFlightPassengersState, state => state.loaded);
export const getFlightPassengersLoading = createSelector(getFlightPassengersState, state => state.loading);
export const getFlightPassengers = createSelector(getFlightPassengersState, state =>
  fromTrip.flightPassengersSelectors.selectAll(state)
);
export const getFlightPassengersHasMore = createSelector(getFlightPassengersState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.flightPassengersSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// passenger logs
const getPassengerLogsState = createSelector(getTripSubState, state => state.passengerLogs);
export const getPassengerLogsLoaded = createSelector(getPassengerLogsState, state => state.loaded);
export const getPassengerLogsLoading = createSelector(getPassengerLogsState, state => state.loading);
export const getPassengerLogs = createSelector(getPassengerLogsState, fromTrip.passengerLogsSelectors.selectAll);
export const getPassengerLogsHasMore = createSelector(getPassengerLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.passengerLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// passenger catering logs
const getPassengerCateringLogsState = createSelector(getTripSubState, state => state.passengerCateringLogs);
export const getPassengerCateringLogsLoaded = createSelector(getPassengerCateringLogsState, state => state.loaded);
export const getPassengerCateringLogsLoading = createSelector(getPassengerCateringLogsState, state => state.loading);
export const getPassengerCateringLogs = createSelector(getPassengerCateringLogsState, fromTrip.passengerCateringLogsSelectors.selectAll);
export const getPassengerCateringLogsHasMore = createSelector(getPassengerCateringLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.passengerCateringLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// passenger hotel logs
const getPassengerHotelLogsState = createSelector(getTripSubState, state => state.passengerHotelLogs);
export const getPassengerHotelLogsLoaded = createSelector(getPassengerHotelLogsState, state => state.loaded);
export const getPassengerHotelLogsLoading = createSelector(getPassengerHotelLogsState, state => state.loading);
export const getPassengerHotelLogs = createSelector(getPassengerHotelLogsState, fromTrip.passengerHotelLogsSelectors.selectAll);
export const getPassengerHotelLogsHasMore = createSelector(getPassengerHotelLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.passengerHotelLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// passenger transportation logs
const getPassengerTransportationLogsState = createSelector(getTripSubState, state => state.passengerTransportationLogs);
export const getPassengerTransportationLogsLoaded = createSelector(getPassengerTransportationLogsState, state => state.loaded);
export const getPassengerTransportationLogsLoading = createSelector(getPassengerTransportationLogsState, state => state.loading);
export const getPassengerTransportationLogs = createSelector(getPassengerTransportationLogsState, fromTrip.passengerTransportationLogsSelectors.selectAll);
export const getPassengerTransportationLogsHasMore = createSelector(getPassengerTransportationLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.passengerTransportationLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// flight schedulers
const getFlightSchedulersState = createSelector(getTripSubState, state => state.flightSchedulers);
export const getFlightSchedulersLoaded = createSelector(getFlightSchedulersState, state => state.loaded);
export const getFlightSchedulersLoading = createSelector(getFlightSchedulersState, state => state.loading);
export const getFlightSchedulers = createSelector(getFlightSchedulersState, state =>
  fromTrip.flightSchedulersSelectors.selectAll(state)
);
export const getFlightSchedulersHasMore = createSelector(getFlightSchedulersState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.flightSchedulersSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});

// scheduler logs
const getSchedulerLogsState = createSelector(getTripSubState, state => state.schedulerLogs);
export const getSchedulerLogsLoaded = createSelector(getSchedulerLogsState, state => state.loaded);
export const getSchedulerLogsLoading = createSelector(getSchedulerLogsState, state => state.loading);
export const getSchedulerLogs = createSelector(getSchedulerLogsState, fromTrip.schedulerLogsSelectors.selectAll);
export const getSchedulerLogsHasMore = createSelector(getSchedulerLogsState, state => {
  if (!state.loaded) return false;
  const loadedCount = fromTrip.schedulerLogsSelectors.selectTotal(state);
  return loadedCount < state.totalCount;
});
